import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, downloadFile } from "../lib/api";
import { inr, quickRange } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileSpreadsheet, FileText, RotateCcw, Paperclip, Printer } from "lucide-react";
import { toast } from "sonner";
import { AttachmentManager } from "../components/AttachmentManager";

const ENTRY_OPTIONS = [
  { v: "", l: "All entries" },
  { v: "cs", l: "Cash Slip" },
  { v: "cv", l: "CV · Direct Expense" },
  { v: "sa", l: "SA · Suspense Advance" },
  { v: "bill", l: "Bill Settlement · CV" },
  { v: "cr", l: "CR · Cash Return" },
  { v: "opening_adjustment", l: "Opening Adjustment" },
];

// Wave review, Important #6: this page carried its own copy of quickRange() built on a UTC isoDate()
// (toISOString()) — wrong for the first five and a half hours of every day in Asia/Kolkata (+05:30). Now
// shared from lib/format.js, on top of the existing local-time todayIso(), so PettyCashReport.jsx (which
// had the identical bug from the identical copy-paste) can't drift from this page again either.

export default function AccountsJournal() {
  const [filters, setFilters] = useState({ from_date: "", to_date: "", person_id: "", category_id: "", entry_type: "", doc_no: "" });
  const [data, setData] = useState(null);
  const [persons, setPersons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attach, setAttach] = useState({ open: false, txnId: null, docNo: null });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([_, v]) => v));
      const { data } = await api.get("/journal", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([api.get("/persons"), api.get("/categories")]);
      setPersons(p.data); setCategories(c.data);
      load({ ...filters });
    })(); // eslint-disable-next-line
  }, []);

  const reset = () => { const f = { from_date: "", to_date: "", person_id: "", category_id: "", entry_type: "", doc_no: "" }; setFilters(f); load(f); };
  const setQuick = (k) => { const [from, to] = quickRange(k); const f = { ...filters, from_date: from, to_date: to }; setFilters(f); load(f); };

  const download = async (kind) => {
    const ext = kind === "xlsx" ? "xlsx" : "pdf";
    try { await downloadFile(`/journal/export.${ext}`, filters, `journal.${ext}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (!data) return <div className="font-mono text-sm text-muted">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="label-eyebrow">Book of Accounts</div>
        <h1 className="font-heading text-4xl font-bold mt-1">Accounts Journal</h1>
        <p className="text-sm text-muted mt-1">Auto-generated book of accounts. Filter by period, person, category, doc number.</p>
      </div>

      <Card className="p-5 border border-line">
        <div className="flex gap-2 flex-wrap mb-4">
          {[["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["fy", "FY (Financial Year)"], ["all", "All Time"]].map(([k, l]) => (
            <Button key={k} variant="outline" size="sm" onClick={() => k === "all" ? (setFilters({ ...filters, from_date: "", to_date: "" }), load({ ...filters, from_date: "", to_date: "" })) : setQuick(k)} data-testid={`quick-${k}`}>{l}</Button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="label-eyebrow">From</Label><Input type="date" value={filters.from_date} onChange={e => setFilters({ ...filters, from_date: e.target.value })} className="mt-1.5 font-mono" data-testid="j-from" /></div>
          <div><Label className="label-eyebrow">To</Label><Input type="date" value={filters.to_date} onChange={e => setFilters({ ...filters, to_date: e.target.value })} className="mt-1.5 font-mono" data-testid="j-to" /></div>
          <div><Label className="label-eyebrow">Person</Label>
            <Select value={filters.person_id || "_all"} onValueChange={v => setFilters({ ...filters, person_id: v === "_all" ? "" : v })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="_all">All persons</SelectItem>{persons.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="label-eyebrow">Category</Label>
            <Select value={filters.category_id || "_all"} onValueChange={v => setFilters({ ...filters, category_id: v === "_all" ? "" : v })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="_all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="label-eyebrow">Entry Type</Label>
            <Select value={filters.entry_type || "_all"} onValueChange={v => setFilters({ ...filters, entry_type: v === "_all" ? "" : v })}>
              <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{ENTRY_OPTIONS.map(o => <SelectItem key={o.v || "_all"} value={o.v || "_all"}>{o.l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="label-eyebrow">Doc No.</Label><Input value={filters.doc_no} onChange={e => setFilters({ ...filters, doc_no: e.target.value })} placeholder="CV 001 / SA 002 / CS 003" className="mt-1.5 font-mono" /></div>
          <div className="flex gap-2 items-end">
            <Button onClick={() => load()} disabled={loading} data-testid="j-apply" className="bg-ink text-white">Apply</Button>
            <Button onClick={reset} variant="outline"><RotateCcw size={14} className="mr-1" /> Reset</Button>
          </div>
          <div className="flex gap-2 items-end justify-end">
            <Button onClick={() => download("xlsx")} variant="outline" data-testid="j-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
            <Button onClick={() => download("pdf")} variant="outline" data-testid="j-pdf"><FileText size={14} className="mr-1" /> PDF</Button>
            <Button asChild variant="outline" data-testid="j-print-vouchers">
              <Link to="/vouchers/print"><Printer size={14} className="mr-1" /> Print Vouchers</Link>
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Opening Cash" v={data.opening_cash} testid="j-open-cash" />
        <Stat label="Opening Suspense" v={data.opening_suspense} testid="j-open-sus" />
        <Stat label="Cash In" v={data.totals.cash_in} accent="in" />
        <Stat label="Cash Out" v={data.totals.cash_out} accent="out" />
        <Stat label="Expense" v={data.totals.expense} />
        <Stat label="Suspense Dr" v={data.totals.sus_dr} />
        <Stat label="Closing Cash" v={data.closing_cash} strong testid="j-close-cash" />
        <Stat label="Closing Suspense" v={data.closing_suspense} strong testid="j-close-sus" />
      </div>

      <Card className="overflow-hidden border border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-paper-alt border-b border-line">
              {["#", "Date", "Time", "Entry", "Doc", "Person", "Particulars", "Category", "In", "Out", "Exp", "S.Dr", "S.Cr", "Cash Bal", "Sus Bal"].map(h => (
                <th key={h} className="text-left px-2.5 py-2.5 label-eyebrow whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody data-testid="journal-rows">
              <tr className="border-b border-line bg-paper-alt/40">
                <td colSpan={13} className="px-2.5 py-2 italic text-muted">Opening (b/f)</td>
                <td className="px-2.5 py-2 text-right font-mono font-semibold">{inr(data.opening_cash)}</td>
                <td className="px-2.5 py-2 text-right font-mono font-semibold">{inr(data.opening_suspense)}</td>
              </tr>
              {data.rows.map((r, i) => (
                <tr key={r.id} className="border-b border-line hover:bg-paper/60">
                  <td className="px-2.5 py-2 font-mono text-muted">{i + 1}</td>
                  <td className="px-2.5 py-2 font-mono">{r.date}</td>
                  <td className="px-2.5 py-2 font-mono text-muted">{(r.created_at || "").slice(11, 16)}</td>
                  <td className="px-2.5 py-2">{r.entry_label}</td>
                  <td className="px-2.5 py-2 font-mono">
                    {r.doc_no || ""}
                    {["cv", "sa", "bill"].includes(r.entry_type) && (
                      <button onClick={() => setAttach({ open: true, txnId: r.id, docNo: r.doc_no })}
                        data-testid={`j-attach-${r.id}`}
                        className={`ml-1.5 align-middle ${r.attachment_count > 0 ? "text-ink" : "text-muted hover:text-ink"}`}
                        title={r.attachment_count > 0 ? `${r.attachment_count} photo(s)` : "Photos"}>
                        <Paperclip size={11} className="inline" />
                        {r.attachment_count > 0 && <span className="text-[10px] ml-0.5">{r.attachment_count}</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-2.5 py-2">{r.person_name || ""}</td>
                  <td className="px-2.5 py-2">{r.particulars}</td>
                  <td className="px-2.5 py-2 text-muted">{r.category_name || ""}</td>
                  <td className="px-2.5 py-2 text-right font-mono text-cashin">{r.cash_in ? inr(r.cash_in) : ""}</td>
                  <td className="px-2.5 py-2 text-right font-mono text-cashout">{r.cash_out ? inr(r.cash_out) : ""}</td>
                  <td className="px-2.5 py-2 text-right font-mono">{r.expense ? inr(r.expense) : ""}</td>
                  <td className="px-2.5 py-2 text-right font-mono text-cashout">{r.sus_dr ? inr(r.sus_dr) : ""}</td>
                  <td className="px-2.5 py-2 text-right font-mono text-cashin">{r.sus_cr ? inr(r.sus_cr) : ""}</td>
                  <td className="px-2.5 py-2 text-right font-mono">{inr(r.running_cash)}</td>
                  <td className="px-2.5 py-2 text-right font-mono">{inr(r.running_suspense)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={15} className="px-2.5 py-10 text-center text-muted">No entries in this filter.</td></tr>}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot>
                <tr className="bg-paper-alt border-t-2 border-line">
                  <td colSpan={8} className="px-2.5 py-2.5 label-eyebrow">Closing</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-semibold">{inr(data.totals.cash_in)}</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-semibold">{inr(data.totals.cash_out)}</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-semibold">{inr(data.totals.expense)}</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-semibold">{inr(data.totals.sus_dr)}</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-semibold">{inr(data.totals.sus_cr)}</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-bold">{inr(data.closing_cash)}</td>
                  <td className="px-2.5 py-2.5 text-right font-mono font-bold">{inr(data.closing_suspense)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <AttachmentManager
        open={attach.open}
        txnId={attach.txnId}
        docNo={attach.docNo}
        onOpenChange={(o) => setAttach((s) => ({ ...s, open: o }))}
        onChanged={() => load()}
      />
    </div>
  );
}

function Stat({ label, v, accent, strong, testid }) {
  const c = accent === "in" ? "text-cashin" : accent === "out" ? "text-cashout" : "text-ink";
  // Same rule as CashBook's StatCard: whitespace-nowrap keeps the figure whole, type scales down instead.
  return (
    <Card className="p-4 border border-line overflow-hidden" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 font-mono tabular-nums whitespace-nowrap ${strong ? "text-lg sm:text-xl font-bold" : "text-sm sm:text-base font-semibold"} ${c}`}>{inr(v)}</div>
    </Card>
  );
}
